import React, { type ButtonHTMLAttributes, forwardRef } from 'react'
import styled from '@emotion/styled'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { Icon } from '@mobi/component-library/Assets/Icon'

const enum LocalConstants {
  MenuItemSelectedClass = 'js-menu__item--selected',
  MenuItemHighlightedClass = 'js-menu__item--highlighted',
}

type MenuProps = {
  children: React.ReactNode
} & React.HTMLAttributes<HTMLUListElement>

export const Menu = forwardRef<HTMLUListElement, MenuProps>(({ children, ...ulProps }, ref) => {
  return (
    <WrapperStyled data-testid='Navigation.Menu'>
      <ul ref={ref} {...ulProps}>
        {children}
      </ul>
    </WrapperStyled>
  )
})

Menu.displayName = 'Menu'

type MenuItemProps = {
  children: React.ReactNode
  isSelected?: boolean
  isHighlighted?: boolean
  showSelectedIcon?: boolean
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>

export const MenuItem = forwardRef<HTMLLIElement, MenuItemProps>(
  (
    {
      isSelected = false,
      isHighlighted = false,
      showSelectedIcon = true,
      children,
      ...buttonProps
    },
    ref
  ) => {
    const classNames = []
    if (isSelected) classNames.push(LocalConstants.MenuItemSelectedClass)
    if (isHighlighted) classNames.push(LocalConstants.MenuItemHighlightedClass)

    return (
      <li ref={ref} className={classNames.join(' ') || undefined}>
        <button type='button' {...buttonProps}>
          <div>{children}</div>
          {showSelectedIcon && isSelected && (
            <span>
              <Icon name='LineCheck' size='2rem' color={colors.studio[500]} />
            </span>
          )}
        </button>
      </li>
    )
  }
)

MenuItem.displayName = 'MenuItem'

// ======
// Styles
// ======

const WrapperStyled = styled.nav({
  '> ul': {
    listStyle: 'none',
    padding: `${spacing.smx2} 0`,
    margin: 0,
  },

  '> ul > li': {
    padding: 0,
    margin: 0,

    '> button': {
      boxSizing: 'border-box',
      display: 'flex',
      cursor: 'pointer',
      alignItems: 'center',
      gap: spacing.smx1,
      width: '100%',
      padding: spacing.sm,
      borderRadius: radius.md,
      color: colors.black,
      border: 0,
      background: 'transparent',

      fontFamily: font.family.primary,
      fontSize: font.size.lg.fontSize,
      letterSpacing: font.size.lg.letterSpacing,
      lineHeight: font.size.lg.lineHeight,
      fontWeight: font.weight.regular,

      '&:hover': {
        background: colors.surface[100],
      },

      // Children container
      '> div': {
        display: 'flex',
        alignItems: 'center',
        gap: spacing.smx1,
      },

      '> span': { display: 'none' },
    },

    [`&.${LocalConstants.MenuItemSelectedClass}`]: {
      '> button': {
        background: colors.surface[50],

        '> span': {
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          marginLeft: 'auto',
        },
      },
    },

    [`&.${LocalConstants.MenuItemHighlightedClass}`]: {
      '> button': {
        background: colors.surface[200],
      },
    },
  },
})
